import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ClipboardList,
  Download,
  FileText,
  ListChecks,
  Loader2,
  MessageSquare,
  ShieldAlert,
  Trophy,
  UserSearch,
  Users,
  UserX,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import { DonutChart, Legend } from '../../components/charts/Charts'
import { teamAnalyticsResponse, employeeDayAuditResponse } from '../../lib/types'

/** Build a Team-Tasks drill-down URL. Truthy values only; `true` → "1".
 *  Every param maps onto a deep-link alias the Team Tasks board understands
 *  (assignee / unassigned / source / verification / status / filter). */
function teamTasksHref(params) {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    qs.set(k, v === true ? '1' : String(v))
  }
  const s = qs.toString()
  return `/app/team-tasks${s ? `?${s}` : ''}`
}

/** Format an integer-percentage rate (null → "—"). */
const fmtPct = (v) => (v == null ? '—' : `${v}%`)

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'to_do', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'complete', label: 'Complete' },
  { value: 'overdue', label: 'Overdue' },
]
const SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'manual', label: 'Manual' },
  { value: 'blueprint', label: 'From SOP (Blueprint)' },
  { value: 'ai', label: 'AI-drafted' },
  { value: 'recurring', label: 'Recurring' },
]
const STATUS_COLORS = ['#94a3b8', '#a855f7', '#10b981', '#ef4444'] // todo/ip/complete/overdue
const SOURCE_COLORS = ['#6366f1', '#0ea5e9', '#94a3b8'] // ai / sop / other

/** Trigger a browser download of a server CSV, honoring the same params + auth. */
async function downloadCsv(path, params, filename) {
  const res = await api.get(path, { params, responseType: 'blob' })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/* Small labelled select — matches the app's simple filter style. */
function FilterSelect({ label, value, onChange, options }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-[color:var(--color-ink-muted)]">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2 text-sm text-[color:var(--color-ink)] cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function StatTile({ icon: Icon, label, value, tone = 'slate' }) {
  const tones = {
    slate: 'text-[color:var(--color-ink-muted)]',
    violet: 'text-[#8b5cf6]',
    sky: 'text-[color:var(--color-info)]',
    emerald: 'text-[color:var(--color-success)]',
    amber: 'text-[color:var(--color-warning)]',
    gold: 'text-[#D97706]',
  }
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
      <div className="flex items-center gap-2">
        <Icon size={16} className={tones[tone]} />
        <span className="text-xs uppercase tracking-wide text-[color:var(--color-ink-muted)]">
          {label}
        </span>
      </div>
      <p className="mt-1 font-display text-2xl tabular-nums text-[color:var(--color-ink-strong)]">
        {value}
      </p>
    </div>
  )
}

/* KPI tile for the accountability strip. Optionally a drill-down Link to the
 * Team Tasks board (the whole tile becomes clickable) with a sublabel. */
function KpiTile({ icon: Icon, label, value, sub, tone = 'slate', to }) {
  const tones = {
    slate: 'text-[color:var(--color-ink-muted)]',
    sky: 'text-[color:var(--color-info)]',
    emerald: 'text-[color:var(--color-success)]',
    amber: 'text-[color:var(--color-warning)]',
    rose: 'text-[color:var(--color-danger)]',
    violet: 'text-[#8b5cf6]',
  }
  const body = (
    <>
      <div className="flex items-center gap-2">
        <Icon size={16} className={tones[tone]} />
        <span className="text-xs uppercase tracking-wide text-[color:var(--color-ink-muted)]">
          {label}
        </span>
      </div>
      <p className="mt-1 font-display text-2xl tabular-nums text-[color:var(--color-ink-strong)] m-0">
        {value}
      </p>
      {sub ? (
        <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)] m-0">{sub}</p>
      ) : null}
    </>
  )
  const cls =
    'block rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4'
  return to ? (
    <Link
      to={to}
      className={cls + ' transition-colors hover:border-[color:var(--color-accent-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer no-underline'}
      title="View these tasks in Team Tasks"
    >
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  )
}

/** A numeric matrix cell that drills into Team Tasks for one employee +
 *  dimension. Zero renders as a muted, non-clickable "0". */
function CellLink({ value, to, danger }) {
  if (!value) return <span className="tabular-nums text-[color:var(--color-ink-muted)]">0</span>
  return (
    <Link
      to={to}
      className={
        'tabular-nums underline decoration-dotted underline-offset-2 hover:decoration-solid cursor-pointer ' +
        (danger ? 'text-[color:var(--color-danger)] font-semibold' : 'text-[color:var(--color-ink-strong)]')
      }
    >
      {value}
    </Link>
  )
}

/* ───────────────────────────── Tab 1: Team Analytics ───────────────────────────── */

function AnalyticsTab({ roster, filterOptions }) {
  // Every filter is SERVER-side: the DTO validates it and the service applies it
  // (narrowing spaces/employees) BEFORE the aggregation, so the matrix, the
  // donuts and the CSV export are always computed over the same authorized,
  // filtered set — no client-side re-filtering that could drift from the export.
  const [dateMode, setDateMode] = useState('created')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  // YYYY-MM-DD keys compare correctly as strings.
  const datesInverted = Boolean(dateFrom && dateTo && dateFrom > dateTo)
  const [status, setStatus] = useState('')
  const [creationSource, setCreationSource] = useState('')
  const [workRoleId, setWorkRoleId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [spaceId, setSpaceId] = useState('')
  const [listId, setListId] = useState('')

  const serverParams = useMemo(() => {
    const p = { dateMode }
    // [QA-15] An inverted range is not sent (the API rejects it); the inline
    // note below explains, and the previous results stay on screen.
    if (!datesInverted) {
      if (dateFrom) p.dateFrom = dateFrom
      if (dateTo) p.dateTo = dateTo
    }
    if (status) p.status = status
    if (creationSource) p.creationSource = creationSource
    if (workRoleId) p.workRoleId = workRoleId
    if (departmentId) p.departmentId = departmentId
    if (spaceId) p.spaceId = spaceId
    if (listId) p.listId = listId
    return p
  }, [dateMode, dateFrom, dateTo, datesInverted, status, creationSource, workRoleId, departmentId, spaceId, listId])

  const query = useQuery({
    queryKey: ['team-analytics', 'tasks', serverParams],
    queryFn: async () => {
      const res = await api.get('/team/analytics/tasks', { params: serverParams })
      return teamAnalyticsResponse.parse(res.data)
    },
    // [QA-22] A filter change is a new key: keep the previous results on
    // screen (dimmed) instead of flashing all-zero tiles + "No tasks in scope."
    placeholderData: keepPreviousData,
  })
  // First load (no data of any kind yet) — tiles/donuts show a loading state;
  // never zeros and never a guessed "company-wide" scope label.
  const firstLoad = !query.data && !query.isError
  const refreshing = query.isPlaceholderData

  const rows = useMemo(() => query.data?.data ?? [], [query.data])
  // Filter-option lists come from the UNFILTERED roster (passed in) so choosing
  // a Work Role / Department never removes the other options as the table narrows.
  const workRoleOptions = useMemo(() => {
    const m = new Map()
    for (const r of roster) if (r.workRoleId) m.set(r.workRoleId, r.workRole ?? '—')
    return [{ value: '', label: 'All work roles' }, ...[...m].map(([value, label]) => ({ value, label }))]
  }, [roster])
  const deptOptions = useMemo(() => {
    const m = new Map()
    for (const r of roster) if (r.departmentId) m.set(r.departmentId, r.department ?? '—')
    return [{ value: '', label: 'All departments' }, ...[...m].map(([value, label]) => ({ value, label }))]
  }, [roster])
  // Only offer a Department filter when there is more than one department to
  // choose BETWEEN. A department-scoped supervisor's roster is their own
  // department only (and a single-department company has just one), so the
  // control would show "All departments" + a single entry — a pointless control
  // that falsely implies company-wide reach. `deptOptions` always carries the
  // "All departments" placeholder, so >2 means ≥2 real departments.
  const showDeptFilter = deptOptions.length > 2
  // Space / List options come from the UNFILTERED scope (page-level roster
  // fetch) so selecting a Space never removes the other Spaces from the picker.
  // Lists narrow to the selected Space client-side.
  const spaceOptions = useMemo(
    () => [
      { value: '', label: 'All spaces' },
      ...(filterOptions?.spaces ?? []).map((s) => ({ value: s.id, label: s.name })),
    ],
    [filterOptions],
  )
  const listOptions = useMemo(
    () => [
      { value: '', label: 'All lists' },
      ...(filterOptions?.lists ?? [])
        .filter((l) => !spaceId || l.spaceId === spaceId)
        .map((l) => ({ value: l.id, label: l.name })),
    ],
    [filterOptions, spaceId],
  )
  // Switching Space invalidates a List chosen in another Space — reset it.
  function onSpaceChange(v) {
    setSpaceId(v)
    setListId('')
  }

  // KPIs + donut segments come straight from the SERVER response (already
  // scoped + filtered), so they always reconcile with the table + the CSV export.
  const kpis = query.data?.kpis ?? {
    total: 0, toDo: 0, inProgress: 0, complete: 0, overdue: 0, unassigned: 0,
    verificationPending: 0, verificationApproved: 0, verificationRejected: 0,
    createdViaSop: 0, createdViaAi: 0, employees: 0, completionRate: null, overdueRate: null,
  }
  const unassigned = query.data?.unassigned ?? null
  const scopeMode = query.data?.scopeMode ?? 'all'
  const statusSegments = (query.data?.statusBreakdown ?? []).map((b, i) => ({
    label: b.label,
    value: b.count,
    color: STATUS_COLORS[i % STATUS_COLORS.length],
  }))
  const sourceSegments = (query.data?.sourceBreakdown ?? []).map((b, i) => ({
    label: b.label,
    value: b.count,
    color: SOURCE_COLORS[i % SOURCE_COLORS.length],
  }))

  const num = (v) => <span className="tabular-nums">{v}</span>
  // Per-employee drill-down base (this employee, honoring nothing else — the
  // board understands assignee + one dimension alias).
  const cellHref = (r, extra) => teamTasksHref({ assignee: r.userId, ...extra })
  const columns = useMemo(
    () => [
      { key: 'employee', header: 'Employee', minWidth: 180, sortAccessor: (r) => r.employee, searchAccessor: (r) => `${r.employee} ${r.email}`, exportValue: (r) => r.employee, render: (r) => (
        <div className="min-w-0">
          <Link to={cellHref(r)} className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate m-0 block hover:underline cursor-pointer" title="View this person's tasks">{r.employee}</Link>
          <p className="text-xs text-[color:var(--color-ink-muted)] truncate m-0">{r.email}</p>
        </div>
      ) },
      { key: 'workRole', header: 'Work Role', minWidth: 120, sortAccessor: (r) => r.workRole ?? '', exportValue: (r) => r.workRole ?? '', render: (r) => r.workRole ?? '—' },
      { key: 'department', header: 'Department', minWidth: 120, sortAccessor: (r) => r.department ?? '', exportValue: (r) => r.department ?? '', render: (r) => r.department ?? '—' },
      { key: 'toDo', header: 'To Do', align: 'center', minWidth: 72, sortAccessor: (r) => r.toDo, exportValue: (r) => r.toDo, render: (r) => num(r.toDo) },
      { key: 'inProgress', header: 'In Progress', align: 'center', minWidth: 90, sortAccessor: (r) => r.inProgress, exportValue: (r) => r.inProgress, render: (r) => num(r.inProgress) },
      { key: 'complete', header: 'Complete', align: 'center', minWidth: 84, sortAccessor: (r) => r.complete, exportValue: (r) => r.complete, render: (r) => <CellLink value={r.complete} to={cellHref(r, { status: 'complete' })} /> },
      { key: 'overdue', header: 'Overdue', align: 'center', minWidth: 80, sortAccessor: (r) => r.overdue, exportValue: (r) => r.overdue, render: (r) => <CellLink value={r.overdue} to={cellHref(r, { filter: 'overdue' })} danger /> },
      { key: 'completionRate', header: 'Compl. %', align: 'center', minWidth: 84, sortAccessor: (r) => r.completionRate ?? -1, exportValue: (r) => (r.completionRate == null ? '' : `${r.completionRate}%`), render: (r) => <span className="tabular-nums text-[color:var(--color-ink-muted)]">{fmtPct(r.completionRate)}</span> },
      { key: 'overdueRate', header: 'Overdue %', align: 'center', minWidth: 88, sortAccessor: (r) => r.overdueRate ?? -1, exportValue: (r) => (r.overdueRate == null ? '' : `${r.overdueRate}%`), render: (r) => (
        <span className={'tabular-nums ' + ((r.overdueRate ?? 0) > 0 ? 'text-[color:var(--color-danger)]' : 'text-[color:var(--color-ink-muted)]')}>{fmtPct(r.overdueRate)}</span>
      ) },
      { key: 'verificationPending', header: 'Pending review', align: 'center', minWidth: 108, sortAccessor: (r) => r.verificationPending, exportValue: (r) => r.verificationPending, render: (r) => <CellLink value={r.verificationPending} to={cellHref(r, { verification: 'pending' })} /> },
      { key: 'createdViaSop', header: 'Via SOP', align: 'center', minWidth: 76, sortAccessor: (r) => r.createdViaSop, exportValue: (r) => r.createdViaSop, render: (r) => <CellLink value={r.createdViaSop} to={cellHref(r, { source: 'blueprint' })} /> },
      { key: 'createdViaAi', header: 'Via AI', align: 'center', minWidth: 72, sortAccessor: (r) => r.createdViaAi, exportValue: (r) => r.createdViaAi, render: (r) => <CellLink value={r.createdViaAi} to={cellHref(r, { source: 'ai' })} /> },
      { key: 'total', header: 'Total', align: 'center', minWidth: 72, sortAccessor: (r) => r.total, exportValue: (r) => r.total, render: (r) => <span className="tabular-nums font-semibold">{r.total}</span> },
    ],
    [],
  )

  const [exporting, setExporting] = useState(false)
  async function exportTasks() {
    setExporting(true)
    try {
      // Export uses the EXACT same server params as the matrix/KPIs, so the CSV
      // row count reconciles with kpis.total for the current filter set.
      await downloadCsv(
        '/team/analytics/tasks/export',
        { ...serverParams },
        `team-tasks-${new Date().toISOString().slice(0, 10)}.csv`,
      )
    } catch (err) {
      // Surface via the toast the app already uses on failures.
      toast.error(extractApiError(err).message)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-5">
      {/* Server filter bar */}
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1 text-xs text-[color:var(--color-ink-muted)]">
            Date basis
            <div className="inline-flex rounded-lg border border-[color:var(--color-rule)] overflow-hidden">
              {['created', 'completed'].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDateMode(m)}
                  className={
                    'h-9 px-3 text-sm capitalize cursor-pointer ' +
                    (dateMode === m
                      ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] font-medium'
                      : 'text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)]')
                  }
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <label className="flex flex-col gap-1 text-xs text-[color:var(--color-ink-muted)]">
            From
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2 text-sm" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-[color:var(--color-ink-muted)]">
            To
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2 text-sm" />
          </label>
          {datesInverted ? (
            <p role="status" className="self-end pb-2 text-xs text-[color:var(--color-warning)]">
              The From date must be on or before the To date.
            </p>
          ) : null}
          <FilterSelect label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
          <FilterSelect label="Creation source" value={creationSource} onChange={setCreationSource} options={SOURCE_OPTIONS} />
          <FilterSelect label="Work role" value={workRoleId} onChange={setWorkRoleId} options={workRoleOptions} />
          {showDeptFilter ? (
            <FilterSelect label="Department" value={departmentId} onChange={setDepartmentId} options={deptOptions} />
          ) : null}
          <FilterSelect label="Space" value={spaceId} onChange={onSpaceChange} options={spaceOptions} />
          <FilterSelect label="List" value={listId} onChange={setListId} options={listOptions} />
          <button
            type="button"
            onClick={exportTasks}
            disabled={exporting || kpis.total === 0}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 text-sm font-medium cursor-pointer hover:bg-[color:var(--color-card-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            Export tasks (CSV)
          </button>
        </div>
      </div>

      {/* Accountability KPI strip — distinct-task counts for the whole scope.
          The clickable tiles drill into Team Tasks on the same dimension. */}
      {query.isError ? null : firstLoad ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2 py-6">
          <Loader2 size={14} className="animate-spin" /> Loading team analytics…
        </p>
      ) : (
        <div
          className={
            'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 transition-opacity ' +
            (refreshing ? 'opacity-60' : '')
          }
          aria-busy={refreshing || undefined}
        >
          <KpiTile
            icon={ListChecks}
            label="Tasks in scope"
            value={kpis.total}
            sub={scopeMode === 'department' ? 'your department' : 'company-wide'}
            tone="slate"
          />
          <KpiTile
            icon={CheckCircle2}
            label="Completion"
            value={fmtPct(kpis.completionRate)}
            sub={`${kpis.complete}/${kpis.total} done`}
            tone="emerald"
            to={kpis.complete ? teamTasksHref({ status: 'complete' }) : undefined}
          />
          <KpiTile
            icon={AlertTriangle}
            label="Overdue"
            value={kpis.overdue}
            sub={`${fmtPct(kpis.overdueRate)} of open`}
            tone="rose"
            to={kpis.overdue ? teamTasksHref({ filter: 'overdue' }) : undefined}
          />
          <KpiTile
            icon={ShieldAlert}
            label="Pending review"
            value={kpis.verificationPending}
            sub={`${kpis.verificationApproved} approved · ${kpis.verificationRejected} rejected`}
            tone="amber"
            to={kpis.verificationPending ? teamTasksHref({ verification: 'pending' }) : undefined}
          />
          <KpiTile
            icon={UserX}
            label="Unassigned"
            value={kpis.unassigned}
            sub={unassigned ? `${unassigned.overdue} overdue` : 'no team assignee'}
            tone="violet"
            to={kpis.unassigned ? teamTasksHref({ unassigned: true }) : undefined}
          />
          <KpiTile
            icon={Users}
            label="Employees"
            value={kpis.employees}
            sub="in scope"
            tone="sky"
          />
        </div>
      )}

      {/* Breakdown donuts */}
      {query.isError || firstLoad ? null : (
        <div
          className={
            'grid grid-cols-1 md:grid-cols-2 gap-4 transition-opacity ' +
            (refreshing ? 'opacity-60' : '')
          }
        >
          <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
            <h3 className="font-display text-base font-medium text-[color:var(--color-ink-strong)] m-0 mb-3">By status</h3>
            {kpis.total === 0 ? (
              <p className="text-sm italic text-[color:var(--color-ink-muted)] py-6 text-center">No tasks in scope.</p>
            ) : (
              <div className="flex flex-wrap items-center gap-6">
                <DonutChart segments={statusSegments} centerLabel="Tasks" centerValue={kpis.total} size={150} thickness={24} />
                <div className="flex-1 min-w-[10rem]"><Legend segments={statusSegments} /></div>
              </div>
            )}
          </div>
          <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
            <h3 className="font-display text-base font-medium text-[color:var(--color-ink-strong)] m-0 mb-3">By creation source</h3>
            {kpis.total === 0 ? (
              <p className="text-sm italic text-[color:var(--color-ink-muted)] py-6 text-center">No tasks in scope.</p>
            ) : (
              <div className="flex flex-wrap items-center gap-6">
                <DonutChart segments={sourceSegments} centerLabel="Tasks" centerValue={kpis.total} size={150} thickness={24} />
                <div className="flex-1 min-w-[10rem]"><Legend segments={sourceSegments} /></div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Unassigned-work accountability banner — only when there IS unassigned
          work in scope, so it reads as an exception, not a permanent widget. */}
      {!query.isError && kpis.unassigned > 0 ? (
        <Link
          to={teamTasksHref({ unassigned: true })}
          className="flex items-center gap-3 rounded-xl border border-[color:var(--color-warning)]/40 bg-[color:var(--color-warning)]/5 p-4 no-underline hover:bg-[color:var(--color-warning)]/10 transition-colors cursor-pointer"
        >
          <UserX size={20} className="text-[color:var(--color-warning)] shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-[color:var(--color-ink-strong)] m-0">
              {kpis.unassigned} task{kpis.unassigned === 1 ? '' : 's'} in scope have no team assignee
            </p>
            <p className="text-xs text-[color:var(--color-ink-muted)] m-0">
              {unassigned ? `${unassigned.overdue} overdue · ${unassigned.complete} complete · ${unassigned.toDo + unassigned.inProgress} open` : 'Review and assign these.'}
            </p>
          </div>
          <span className="text-xs font-medium text-[color:var(--color-accent-strong)] shrink-0">View →</span>
        </Link>
      ) : null}

      {/* Employee matrix */}
      <DataTable
        tableId="team-analytics-matrix"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.userId}
        loading={query.isLoading}
        error={query.isError ? extractApiError(query.error).message : null}
        onRetry={() => query.refetch()}
        searchPlaceholder="Search employee"
        title="Team Task Analytics"
        exportBaseName="team-analytics"
        exportTitle="Team Task Analytics"
        emptyState={{
          icon: BarChart3,
          title: rows.length === 0 ? 'No employees in scope' : 'No employees match your filters',
        }}
      />
    </div>
  )
}

/* ───────────────────────────── Tab 2: Employee Audit ───────────────────────────── */

const KIND_META = {
  task_created: { icon: ClipboardList, tone: 'text-[color:var(--color-info)]', label: 'Created' },
  task_completed: { icon: CheckCircle2, tone: 'text-[color:var(--color-success)]', label: 'Completed' },
  task_event: { icon: FileText, tone: 'text-[color:var(--color-ink-muted)]', label: 'Task update' },
  comment: { icon: MessageSquare, tone: 'text-[#8b5cf6]', label: 'Comment' },
  report: { icon: FileText, tone: 'text-[color:var(--color-warning)]', label: 'Report' },
  points: { icon: Trophy, tone: 'text-[#D97706]', label: 'Points' },
}

function AuditTab({ employees }) {
  const [userId, setUserId] = useState('')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))

  const query = useQuery({
    queryKey: ['team-analytics', 'audit', userId, date],
    enabled: Boolean(userId && date),
    queryFn: async () => {
      const res = await api.get('/team/analytics/audit', { params: { userId, date } })
      return employeeDayAuditResponse.parse(res.data)
    },
  })

  const [exporting, setExporting] = useState(false)
  async function exportAudit() {
    setExporting(true)
    try {
      await downloadCsv(
        '/team/analytics/audit/export',
        { userId, date },
        `employee-audit-${date}.csv`,
      )
    } catch (err) {
      toast.error(extractApiError(err).message)
    } finally {
      setExporting(false)
    }
  }

  const s = query.data?.summary
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-[color:var(--color-ink-muted)] min-w-[220px]">
          Employee
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="h-9 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2 text-sm cursor-pointer"
          >
            <option value="">Select an employee…</option>
            {employees.map((e) => (
              <option key={e.userId} value={e.userId}>
                {e.employee}
                {e.workRole ? ` · ${e.workRole}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-[color:var(--color-ink-muted)]">
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2 text-sm" />
        </label>
        <button
          type="button"
          onClick={exportAudit}
          disabled={exporting || !query.data || query.data.timeline.length === 0}
          className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 text-sm font-medium cursor-pointer hover:bg-[color:var(--color-card-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
          Export day (CSV)
        </button>
      </div>

      {!userId ? (
        <div className="rounded-xl border border-dashed border-[color:var(--color-rule)] p-10 text-center text-sm text-[color:var(--color-ink-muted)]">
          <UserSearch size={22} className="mx-auto mb-2 opacity-60" />
          Pick an employee and a date to see everything they did that day.
        </div>
      ) : query.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading audit…</p>
      ) : query.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">{extractApiError(query.error).message}</p>
      ) : query.data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatTile icon={ClipboardList} label="Created" value={s.tasksCreated} tone="sky" />
            <StatTile icon={CheckCircle2} label="Completed" value={s.tasksCompleted} tone="emerald" />
            <StatTile icon={MessageSquare} label="Comments" value={s.comments} tone="violet" />
            <StatTile icon={FileText} label="Reports" value={s.reportsSubmitted} tone="amber" />
            <StatTile icon={Trophy} label="Points" value={s.pointsEarned} tone="gold" />
          </div>

          <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
            <h3 className="font-display text-base font-medium text-[color:var(--color-ink-strong)] m-0 mb-3">
              Activity timeline — {query.data.employee.name}, {query.data.date}
            </h3>
            {query.data.timeline.length === 0 ? (
              <p className="text-sm italic text-[color:var(--color-ink-muted)] py-6 text-center">No recorded activity on this date.</p>
            ) : (
              <ul className="space-y-0 divide-y divide-[color:var(--color-rule)]">
                {query.data.timeline.map((e, i) => {
                  const meta = KIND_META[e.kind] ?? { icon: FileText, tone: 'text-[color:var(--color-ink-muted)]', label: e.kind }
                  const Icon = meta.icon
                  return (
                    <li key={i} className="flex items-start gap-3 py-2.5">
                      <span className="mt-0.5 shrink-0"><Icon size={16} className={meta.tone} /></span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[color:var(--color-ink-strong)] m-0">
                          {e.detail}
                          {e.taskName ? <span className="text-[color:var(--color-ink-muted)]"> — {e.taskName}</span> : null}
                        </p>
                      </div>
                      <time className="shrink-0 text-xs text-[color:var(--color-ink-muted)] tabular-nums">
                        {formatDateTime(e.at)}
                      </time>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </div>
  )
}

/* ───────────────────────────── Page shell ───────────────────────────── */

export function TeamAnalyticsPage() {
  const [tab, setTab] = useState('analytics')

  // Shared roster (for the audit employee picker) — the unfiltered breakdown.
  const rosterQuery = useQuery({
    queryKey: ['team-analytics', 'roster'],
    queryFn: async () => {
      const res = await api.get('/team/analytics/tasks', { params: { dateMode: 'created' } })
      return teamAnalyticsResponse.parse(res.data)
    },
  })
  const employees = rosterQuery.data?.data ?? []
  // Space/List picker options from the UNFILTERED scope, so selecting a Space
  // never removes the other Spaces from the dropdown (mirrors the role/dept
  // options, which come from the unfiltered roster too).
  const filterOptions = rosterQuery.data?.filterOptions ?? { spaces: [], lists: [] }

  // Whole-page permission gate: a viewer without department- or company-wide
  // task visibility gets a 403 from the roster. [QA-18] Keyed on the HTTP
  // status — it used to regex the message, which never matched the API's
  // wording, so such a viewer got the filter bar, an Export button, zeroed
  // tiles and a futile "Try Again" instead of this panel.
  const forbidden =
    rosterQuery.isError && rosterQuery.error?.response?.status === 403

  return (
    <div>
      <Breadcrumb current="Team Analytics" homePath="/app" homeLabel="Dashboard" />
      <div className="mb-5">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <BarChart3 size={26} className="text-[color:var(--color-accent-strong)]" />
          Team Analytics
        </h1>
        <p className="mt-1 text-[color:var(--color-ink-muted)]">
          Task progress and creation-source breakdown across your team, plus a per-employee day audit.
        </p>
      </div>

      {forbidden ? (
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-10 text-center">
          <UserSearch size={26} className="mx-auto mb-3 text-[color:var(--color-ink-muted)]" />
          <p className="text-sm text-[color:var(--color-ink-strong)] font-medium m-0">Team analytics isn't available for your role.</p>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            You need department- or company-wide task visibility to view team analytics.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4 inline-flex rounded-lg border border-[color:var(--color-rule)] overflow-hidden">
            {[
              { key: 'analytics', label: 'Team Analytics', icon: BarChart3 },
              { key: 'audit', label: 'Employee Audit', icon: UserSearch },
            ].map((t) => {
              const Icon = t.icon
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={
                    'inline-flex items-center gap-2 h-9 px-4 text-sm cursor-pointer ' +
                    (tab === t.key
                      ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] font-medium'
                      : 'text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)]')
                  }
                >
                  <Icon size={15} />
                  {t.label}
                </button>
              )
            })}
          </div>

          {tab === 'analytics' ? <AnalyticsTab roster={employees} filterOptions={filterOptions} /> : <AuditTab employees={employees} />}
        </>
      )}
    </div>
  )
}
